import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import BoardList from './BoardList';
import BoardDetail from './BoardDetail';
import BoardEditor from './BoardEditor';

/* ═══════════════════════════════════════
   BoardView — 메인 게시판 컨트롤러 (v2)
   NewsBoard를 대체하는 올인원 게시판 컴포넌트
   ═══════════════════════════════════════ */

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const BoardWrapper = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  animation: ${fadeIn} 0.3s ease both;
`;

// ── View Modes ──
const MODE = {
  LIST: 'list',
  DETAIL: 'detail',
  WRITE: 'write',
  EDIT: 'edit',
};

const PAGE_SIZE = 10;

function BoardView({
  // API 엔드포인트들 (기존 NewsBoard와 동일)
  apiCreateSession,
  apiFetchPage,
  apiFetchPageList,
  apiReadNews,
  apiReadComments,
  apiIncreaseCount,
  apiDeleteNews,
  apiDeleteComment,
  apiInsertComment,
  // 외부 상태 연동
  setNum,
  // 글 쓰기/수정 API (WriteMode 패턴)
  apiInsertNews = '/request/InsertNewsBoard',
  apiUpdateNews = '/request/UpdateNewsBoard',
}) {
  // ── State ──
  const [mode, setMode] = useState(MODE.LIST);
  const [session, setSession] = useState(null);
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);
  const [editData, setEditData] = useState(null);
  const [comments, setComments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [pageInfo, setPageInfo] = useState({
    currentPage: 1,
    totalPages: 0,
    currentMin: 0,
    currentMax: PAGE_SIZE,
    pageNumbers: [],
    hasNext: false,
  });

  // ── Init ──
  useEffect(() => {
    (async () => {
      await handleCreateSession();
      await handleFetchPosts(0, PAGE_SIZE);
      await handleFetchPageList(1);
    })();
    // eslint-disable-next-line
  }, []);

  // Reset when API endpoints change (tab switch)
  useEffect(() => {
    setMode(MODE.LIST);
    setCurrentPost(null);
    setEditData(null);
    setComments([]);
    setSearchQuery('');
    (async () => {
      await handleFetchPosts(0, PAGE_SIZE);
      await handleFetchPageList(1);
    })();
    // eslint-disable-next-line
  }, [apiFetchPage]);

  // ── Session ──
  const handleCreateSession = useCallback(async () => {
    if (!apiCreateSession) return;
    try {
      const res = await fetch(apiCreateSession, { credentials: 'include' });
      const data = await res.json();
      setSession(data);
      return data;
    } catch (err) {
      console.error('Failed to create session:', err);
    }
  }, [apiCreateSession]);

  // ── Fetch Posts ──
  const handleFetchPosts = useCallback(
    async (min, max) => {
      if (!apiFetchPage) return;
      try {
        setPageInfo((prev) => ({ ...prev, currentMin: min, currentMax: max }));
        const response = await fetch(apiFetchPage, {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ MinNumber: min, MaxNumber: max }),
        });
        const data = await response.json();
        setPosts(data);
      } catch (err) {
        console.error('Failed to fetch posts:', err);
        setPosts([]);
      }
    },
    [apiFetchPage]
  );

  // ── Fetch Page List (Pagination) ──
  const handleFetchPageList = useCallback(
    async (page) => {
      if (!apiFetchPageList) return;
      try {
        const response = await fetch(apiFetchPageList, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Data: 'Data' }),
        });
        const info = await response.json();

        if (!info || info.length === 0) return;

        const itemcount = info[0].itemcount ?? 0;
        const total = Math.ceil(itemcount / PAGE_SIZE);
        const pagesArray = Array.from({ length: total }, (_, i) => i + 1);

        const start = (page - 1) * 10 + 1;
        const end = page * 10;
        let filtered = pagesArray.filter((num) => num >= start && num <= end);

        if (total === 1 && filtered.length === 0) {
          filtered = [1];
        }

        const hasNext = filtered.length
          ? filtered[filtered.length - 1] < total
          : false;

        setPageInfo((prev) => ({
          ...prev,
          currentPage: page,
          totalPages: total,
          pageNumbers: filtered,
          hasNext,
        }));
      } catch (err) {
        console.error('Failed to fetch page list:', err);
      }
    },
    [apiFetchPageList]
  );

  // ── Read Post Detail ──
  const handleReadPost = useCallback(
    async (post) => {
      try {
        // 같은 글 다시 클릭 -> 목록으로
        if (currentPost?.num === post.num && mode === MODE.DETAIL) {
          setMode(MODE.LIST);
          setCurrentPost(null);
          return;
        }

        if (setNum) setNum(post.num);

        const res = await fetch(apiReadNews, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ num: post.num }),
        });
        const detailResult = await res.json();

        if (!detailResult || detailResult.length === 0) {
          console.warn('No detail data found for post:', post.num);
          return;
        }

        setCurrentPost(detailResult[0]);
        setMode(MODE.DETAIL);

        // 조회수 증가 + 댓글 로드 (병렬)
        await Promise.all([
          handleIncreaseViewCount(post),
          handleFetchComments(post),
        ]);
      } catch (err) {
        console.error('Failed to read content:', err);
      }
    },
    [apiReadNews, currentPost, mode, setNum]
  );

  // ── Increase View Count ──
  const handleIncreaseViewCount = useCallback(
    async (post) => {
      if (!apiIncreaseCount) return;
      try {
        await fetch(apiIncreaseCount, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(post),
        });
      } catch (err) {
        console.error('Failed to update view count:', err);
      }
    },
    [apiIncreaseCount]
  );

  // ── Fetch Comments ──
  const handleFetchComments = useCallback(
    async (post) => {
      if (!apiReadComments) return;
      try {
        const response = await fetch(apiReadComments, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(post),
        });
        const data = await response.json();
        setComments(data);
      } catch (err) {
        console.error('Failed to fetch comments:', err);
        setComments([]);
      }
    },
    [apiReadComments]
  );

  // ── Delete Post ──
  const handleDeletePost = useCallback(
    async (post) => {
      if (!window.confirm('정말 삭제하시겠습니까?')) return;
      try {
        await fetch(apiDeleteNews, {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(post),
        });
        // 삭제 후 목록으로
        setMode(MODE.LIST);
        setCurrentPost(null);
        await handleFetchPosts(pageInfo.currentMin, pageInfo.currentMax);
      } catch (err) {
        console.error('Failed to delete post:', err);
      }
    },
    [apiDeleteNews, handleFetchPosts, pageInfo.currentMin, pageInfo.currentMax]
  );

  // ── Insert Comment ──
  const handleInsertComment = useCallback(
    async (commentText) => {
      if (!currentPost || !apiInsertComment) return;
      try {
        await fetch(apiInsertComment, {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            noticeNum: currentPost.num,
            commentText: commentText,
          }),
        });
        // 댓글 등록 후 재조회
        await handleFetchComments(currentPost);
      } catch (err) {
        alert('댓글 등록 오류: ' + err);
      }
    },
    [apiInsertComment, currentPost, handleFetchComments]
  );

  // ── Delete Comment ──
  const handleDeleteComment = useCallback(
    async (commentItem) => {
      if (!apiDeleteComment) return;
      try {
        await fetch(apiDeleteComment, {
          method: 'POST',
          mode: 'cors',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(commentItem),
        });
        await handleFetchComments(currentPost);
      } catch (err) {
        alert('댓글 삭제 오류: ' + err);
      }
    },
    [apiDeleteComment, currentPost, handleFetchComments]
  );

  // ── Page Change ──
  const handlePageChange = useCallback(
    (pageNum) => {
      setPageInfo((prev) => ({ ...prev, currentPage: pageNum }));
      handleFetchPosts((pageNum - 1) * PAGE_SIZE, pageNum * PAGE_SIZE);
      handleFetchPageList(pageNum);
      // 페이지 바뀌면 상세 닫기
      setMode(MODE.LIST);
      setCurrentPost(null);
    },
    [handleFetchPosts, handleFetchPageList]
  );

  // ── Submit New / Edit Post ──
  const handleSubmitPost = useCallback(
    async (data) => {
      try {
        if (mode === MODE.EDIT && editData) {
          // 수정
          await fetch(apiUpdateNews, {
            method: 'POST',
            mode: 'cors',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              NUM: data.NUM,
              title: data.title,
              contents: data.contents,
            }),
          });
        } else {
          // 새 글
          await fetch(apiInsertNews, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
          });
        }

        // 작성/수정 완료 후 목록으로
        setMode(MODE.LIST);
        setEditData(null);
        setCurrentPost(null);
        await handleFetchPosts(pageInfo.currentMin, pageInfo.currentMax);
        await handleFetchPageList(pageInfo.currentPage);
      } catch (err) {
        alert('저장 오류: ' + err);
      }
    },
    [
      mode,
      editData,
      apiInsertNews,
      apiUpdateNews,
      handleFetchPosts,
      handleFetchPageList,
      pageInfo,
    ]
  );

  // ── Mode Handlers ──
  const handleWriteClick = useCallback(() => {
    setEditData(null);
    setMode(MODE.WRITE);
  }, []);

  const handleEditClick = useCallback(() => {
    setEditData(currentPost);
    setMode(MODE.EDIT);
  }, [currentPost]);

  const handleCancelEditor = useCallback(() => {
    setEditData(null);
    if (currentPost) {
      setMode(MODE.DETAIL);
    } else {
      setMode(MODE.LIST);
    }
  }, [currentPost]);

  const handleCloseDetail = useCallback(() => {
    setMode(MODE.LIST);
    setCurrentPost(null);
    // 목록 새로고침
    handleFetchPosts(pageInfo.currentMin, pageInfo.currentMax);
  }, [handleFetchPosts, pageInfo.currentMin, pageInfo.currentMax]);

  // ── Render ──
  return (
    <BoardWrapper>
      {/* Detail View */}
      {mode === MODE.DETAIL && currentPost && (
        <BoardDetail
          post={currentPost}
          comments={comments}
          session={session}
          onClose={handleCloseDetail}
          onEdit={handleEditClick}
          onDelete={() => handleDeletePost(currentPost)}
          onInsertComment={handleInsertComment}
          onDeleteComment={handleDeleteComment}
        />
      )}

      {/* Editor (Write / Edit) */}
      {(mode === MODE.WRITE || mode === MODE.EDIT) && (
        <BoardEditor
          editData={editData}
          session={session}
          onSubmit={handleSubmitPost}
          onCancel={handleCancelEditor}
        />
      )}

      {/* List View — always shown (목록은 항상 보임) */}
      {(mode === MODE.LIST || mode === MODE.DETAIL) && (
        <BoardList
          posts={posts}
          pageInfo={pageInfo}
          session={session}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onReadPost={handleReadPost}
          onDeletePost={handleDeletePost}
          onPageChange={handlePageChange}
          onWriteClick={handleWriteClick}
        />
      )}
    </BoardWrapper>
  );
}

export default BoardView;
